import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { crmAPI, eventBatchAPI, trekAPI } from '../services/api';
import { REGISTRATION_LEAD_STATUS_OPTIONS } from '../constants/registrationLeadStatuses.js';
import {
  CATEGORY_LABEL,
  MANUAL_LEAD_TYPE_OPTIONS,
  formatBatchDates
} from '../utils/leadRequestDisplay.js';
import {
  leadEditBatchListParams,
  mergeCurrentBatchForLeadSelect
} from '../utils/leadRequestEditBatches.js';
import { isValidPhoneInput, normalizePhoneInput, phoneInputProps } from '../utils/phoneInput.js';

function batchOptionLabel(batch) {
  const dates = formatBatchDates(batch.startDate, batch.endDate);
  const code = batch.code ? ` (${batch.code})` : '';
  return `${dates}${code}`;
}

const EMPTY_FORM = {
  leadType: 'office_visit',
  name: '',
  phone: '',
  reason: '',
  message: '',
  trekId: '',
  batchId: '',
  leadStatus: ''
};

export default function CrmLeadFormDialog({
  open,
  onClose,
  onSaved,
  mode = 'create',
  initial = null,
  title: titleProp
}) {
  const isIncoming = mode === 'incoming';
  const isCreate = mode === 'create';
  const [form, setForm] = useState(EMPTY_FORM);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { data: reasonsRes } = useQuery(['crm-reasons'], () => crmAPI.getReasons(), { enabled: open });
  const reasons = reasonsRes?.data?.data || [];
  const reasonOptions = useMemo(() => {
    const cur = String(form.reason || '').trim();
    if (!cur || reasons.includes(cur)) return reasons;
    return [...reasons, cur];
  }, [reasons, form.reason]);

  const { data: treksRes } = useQuery(
    ['crm-treks'],
    () => trekAPI.list({ page: 1, limit: 500, isActive: true }),
    { enabled: open }
  );

  const { data: batchesRes, isLoading: batchesLoading } = useQuery(
    ['crm-event-batches', form.trekId],
    () => eventBatchAPI.list(leadEditBatchListParams(form.trekId)),
    { enabled: open && !isCreate && !!form.trekId }
  );

  const batches = batchesRes?.data?.data || batchesRes?.data?.rows || [];

  const treks = useMemo(() => {
    const rows = treksRes?.data?.data || [];
    return [...rows].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    );
  }, [treksRes]);

  const selectedTrek = treks.find((t) => String(t.id) === String(form.trekId)) ?? null;
  const batchesForTrek = useMemo(
    () =>
      mergeCurrentBatchForLeadSelect(batches, {
        batchId: form.batchId,
        lead: initial
      }),
    [batches, form.batchId, initial]
  );

  const categoryLabel = isIncoming
    ? CATEGORY_LABEL.incoming_call
    : CATEGORY_LABEL[form.leadType] || form.leadType;

  useEffect(() => {
    if (!open) return;
    setError('');
    if (initial) {
      setForm({
        leadType: initial.leadType || 'incoming_call',
        name: initial.name || '',
        phone: normalizePhoneInput(initial.phone),
        reason: initial.reason || '',
        message: initial.message || '',
        trekId: initial.trekId
          ? String(initial.trekId)
          : initial.latestTrek?.id
            ? String(initial.latestTrek.id)
            : '',
        batchId: initial.batchId ? String(initial.batchId) : '',
        leadStatus: initial.leadStatus || ''
      });
      if (initial.phone && isIncoming) {
        runLookup(initial.phone);
      }
    } else {
      setForm({ ...EMPTY_FORM });
    }
  }, [open, initial, isIncoming]);

  useEffect(() => {
    if (!open || !initial?.batchId) return;
    if (form.trekId) return;
    if (initial.trekId) {
      setForm((p) => ({ ...p, trekId: String(initial.trekId) }));
    }
  }, [open, initial?.batchId, initial?.trekId, form.trekId]);

  const runLookup = async (phone) => {
    const digits = normalizePhoneInput(phone);
    if (digits.length !== 10) return;
    setLookupLoading(true);
    try {
      const res = await crmAPI.lookupPhone(digits);
      const d = res?.data?.data;
      if (!d) return;
      setForm((prev) => ({
        ...prev,
        phone: digits,
        name: prev.name || d.name || '',
        trekId:
          prev.trekId ||
          (d.latestTrek?.id ? String(d.latestTrek.id) : '')
      }));
    } catch {
      // optional enrichment
    } finally {
      setLookupLoading(false);
    }
  };

  const handlePhoneBlur = () => {
    if (form.phone.length === 10) runLookup(form.phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !isValidPhoneInput(form.phone)) {
      setError('Name and a valid 10-digit mobile number (starting 6–9) are required.');
      return;
    }
    setSaving(true);
    try {
      const trek = selectedTrek;
      const payload = isCreate
        ? {
            leadType: form.leadType,
            name: form.name.trim(),
            phone: normalizePhoneInput(form.phone),
            reason: form.reason || undefined,
            message: form.message.trim() || undefined,
            eventSlug: trek?.slug || undefined,
            eventTitle: trek?.name || undefined,
            status: 'pending'
          }
        : {
            leadType: form.leadType,
            name: form.name.trim(),
            phone: normalizePhoneInput(form.phone),
            reason: form.reason || undefined,
            batchId: form.batchId ? Number(form.batchId) : null,
            eventSlug: trek?.slug || undefined,
            eventTitle: trek?.name || undefined,
            status: isIncoming ? 'contacted' : 'pending',
            leadStatus: form.leadStatus || null
          };
      if (isCreate) {
        await crmAPI.createLead(payload);
      } else if (isIncoming && initial?.source === 'crm_leads') {
        await crmAPI.saveLead(initial.id, payload);
        await crmAPI.popupSeen(initial.id);
      } else if (initial?.leadType && initial?.id) {
        await crmAPI.updateLead(initial.leadType, initial.id, payload);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const dialogTitle =
    titleProp ||
    (isCreate ? 'Create manual lead' : isIncoming ? 'Incoming call — log details' : 'Edit lead');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{dialogTitle}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ pt: 2.5, pb: 2.5 }}>
          {error ? (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          ) : null}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isCreate ? (
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={form.leadType}
                onChange={(e) => setForm((p) => ({ ...p, leadType: e.target.value }))}
              >
                {MANUAL_LEAD_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Category"
              value={categoryLabel}
              disabled
            />
          )}

          <TextField
            fullWidth
            size="small"
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            autoFocus={isCreate}
          />

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              label="Phone"
              required
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: normalizePhoneInput(e.target.value) }))}
              onBlur={handlePhoneBlur}
              inputProps={phoneInputProps}
              helperText="10-digit mobile starting with 6–9"
            />
            {lookupLoading ? <CircularProgress size={22} /> : null}
          </Box>

          <Autocomplete
            size="small"
            options={treks}
            getOptionLabel={(t) => t?.name ?? ''}
            isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
            value={selectedTrek}
            onChange={(_, trek) =>
              setForm((p) => ({
                ...p,
                trekId: trek ? String(trek.id) : '',
                batchId: ''
              }))
            }
            noOptionsText="No treks found"
            ListboxProps={{ style: { maxHeight: 280 } }}
            renderInput={(params) => (
              <TextField {...params} label="Trek" placeholder="Search trek…" />
            )}
          />

          <FormControl fullWidth size="small">
            <InputLabel id="crm-lead-reason-label" shrink>
              Reason
            </InputLabel>
            <Select
              labelId="crm-lead-reason-label"
              label="Reason"
              displayEmpty
              notched
              value={form.reason}
              onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
              renderValue={(selected) =>
                selected ? selected : <em style={{ color: '#94a3b8' }}>—</em>
              }
            >
              <MenuItem value="">—</MenuItem>
              {reasonOptions.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isCreate ? (
            <TextField
              fullWidth
              size="small"
              label="Notes (optional)"
              value={form.message}
              onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
              multiline
              minRows={2}
            />
          ) : null}

          {!isCreate ? (
            <>
              <FormControl
                fullWidth
                size="small"
                disabled={!form.trekId || batchesLoading}
              >
                <InputLabel id="crm-lead-batch-label" shrink>
                  Date of event
                </InputLabel>
                <Select
                  labelId="crm-lead-batch-label"
                  label="Date of event"
                  displayEmpty
                  notched
                  value={form.batchId}
                  onChange={(e) => setForm((p) => ({ ...p, batchId: e.target.value }))}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <em style={{ color: '#94a3b8' }}>
                          {!form.trekId
                            ? 'Select trek first'
                            : batchesLoading
                              ? 'Loading…'
                              : '— Select batch —'}
                        </em>
                      );
                    }
                    const batch = batchesForTrek.find((b) => String(b.id) === String(selected));
                    return batch ? batchOptionLabel(batch) : String(selected);
                  }}
                >
                  <MenuItem value="">
                    <em>
                      {!form.trekId
                        ? 'Select trek first'
                        : batchesLoading
                          ? 'Loading…'
                          : '— Select batch —'}
                    </em>
                  </MenuItem>
                  {batchesForTrek.map((b) => (
                    <MenuItem key={b.id} value={String(b.id)}>
                      {batchOptionLabel(b)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label="Lead Date"
                value={
                  initial?.createdAt ? new Date(initial.createdAt).toLocaleString() : '—'
                }
                disabled
              />

              <FormControl fullWidth size="small">
                <InputLabel id="crm-lead-status-label" shrink>
                  Lead status
                </InputLabel>
                <Select
                  labelId="crm-lead-status-label"
                  label="Lead status"
                  displayEmpty
                  notched
                  value={form.leadStatus}
                  onChange={(e) => setForm((p) => ({ ...p, leadStatus: e.target.value }))}
                  renderValue={(selected) => {
                    if (!selected) return <em style={{ color: '#94a3b8' }}>Not set</em>;
                    const opt = REGISTRATION_LEAD_STATUS_OPTIONS.find((o) => o.value === selected);
                    return opt?.label || selected;
                  }}
                >
                  <MenuItem value="">
                    <em>Not set</em>
                  </MenuItem>
                  {REGISTRATION_LEAD_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={onClose} disabled={saving} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
